// components/ReuploadNotice.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function ReuploadNotice() {
  const { user, token } = useAuth();
  const [showNotice, setShowNotice] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    const checkUserDocuments = async () => {
      try {
        setIsLoading(true);
        console.log('🔍 Starting document status check...');
        console.log('👤 User ID:', user?.id);
        console.log('🔑 Token exists:', !!token);
        console.log('🌐 API URL:', `${process.env.REACT_APP_API_URL}/user/documents-status`);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/user/documents-status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);
        
        if (response.ok) {
          const data = await response.json();
          console.log('📊 Full API response:', data);
          console.log('❓ requiresReupload:', data.requiresReupload);
          
          setShowNotice(data.requiresReupload);
          setDebugInfo(`requiresReupload: ${data.requiresReupload}, hasProfilePhoto: ${data.hasProfilePhoto}, hasIdDocument: ${data.hasIdDocument}`);
        } else {
          console.error('❌ API Error - Status:', response.status);
          const errorText = await response.text();
          console.error('❌ API Error - Body:', errorText);
          setDebugInfo(`API Error: ${response.status} - ${errorText}`);
        }
      } catch (error) {
        console.error('💥 Network error:', error);
        setDebugInfo(`Network Error: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (user && token) {
      checkUserDocuments();
    } else {
      console.log('👤 Missing requirements:', { user: !!user, token: !!token });
      setDebugInfo('Missing user or token');
      setIsLoading(false);
    }
  }, [user, token]);

  // For debugging - always show something
  if (process.env.NODE_ENV === 'development') {
    return (
      <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-semibold text-purple-800 mb-2">Document Status Debug</h3>
            <div className="text-sm text-purple-700 space-y-1">
              <div>Loading: {isLoading ? 'Yes' : 'No'}</div>
              <div>Show Notice: {showNotice ? 'Yes' : 'No'}</div>
              <div>User ID: {user?.id || 'None'}</div>
              <div>Token: {token ? 'Exists' : 'Missing'}</div>
              <div>Debug: {debugInfo}</div>
            </div>
          </div>
          {showNotice && (
            <Link
              to="/recipient/reupload-documents"
              className="inline-flex items-center px-3 py-1 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition-colors"
            >
              Re-upload
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Production version
  if (isLoading) {
    return (
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
          <span className="text-blue-700">Checking document status...</span>
        </div>
      </div>
    );
  }

  if (!showNotice) return null;

  return (
    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg relative">
      <button
        onClick={() => setShowNotice(false)}
        className="absolute top-3 right-3 text-yellow-600 hover:text-yellow-800"
      >
        <XMarkIcon className="w-5 h-5" />
      </button>
      
      <div className="flex items-start">
        <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-yellow-800 mb-1">
            Action Required: Re-upload Your Documents
          </h3>
          <p className="text-yellow-700 text-sm mb-3">
            Due to a technical issue, your previously uploaded documents and profile picture were lost. 
            Please re-upload them to ensure uninterrupted service. Maximum file size: 5MB per file.
          </p>
          <Link
            to="/recipient/reupload-documents"
            className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 transition-colors"
          >
            Re-upload Documents
          </Link>
        </div>
      </div>
    </div>
  );
}